import { useRoute, useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { ChatRoom } from "@/components/chat/ChatRoom";
import { useEffect, useState, useCallback } from "react";
import { PageContainer } from "@/components/layout/PageContainer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2 } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { GoogleAd } from "@/components/GoogleAd";

interface ChatInfo {
  id: string;
  listing_id: string;
  seller_id: string;
  buyer_id: string;
  state: string;
  listing?: {
    id: number;
    title: string;
    priceCents: number | null;
    images: string[];
  };
  seller?: {
    id: string;
    name: string;
    avatarUrl: string | null;
  };
  buyer?: {
    id: string;
    name: string;
    avatarUrl: string | null;
  };
}

export default function ChatRoomPage() {
  const [match, params] = useRoute<{ chatId: string }>("/chat/:chatId");
  const [, setLocation] = useLocation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Extract chatId early to avoid reference errors
  const chatId = match && params ? params.chatId : null;

  // Get current user's database ID (Firebase UID -> DB user ID mapping)
  const { data: userInfo } = useQuery({
    queryKey: ["/api/me"],
    queryFn: async () => {
      if (!user) return null;
      const token = await user.getIdToken();
      const res = await fetch("/api/me", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error("Failed to fetch user info");
      return res.json();
    },
    enabled: !!user,
  });

  // Fetch chat info
  const { data: chatInfo, isLoading: chatLoading } = useQuery<ChatInfo>({
    queryKey: ["/api/chat/info", chatId],
    queryFn: async () => {
      if (!chatId || !user) throw new Error("Missing chat ID or user");
      const token = await user.getIdToken();
      const res = await fetch(`/api/chat/info?chat_id=${chatId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error("Failed to fetch chat info");
      return res.json();
    },
    enabled: !!chatId && !!user,
  });

  // Set current user ID (use Firebase UID for now, as chat API uses Firebase UID)
  useEffect(() => {
    if (user?.uid) {
      setCurrentUserId(user.uid);
    }
  }, [user]);

  // Calculate isParticipant early (before it's used in useEffect)
  const isParticipant = chatInfo
    ? chatInfo.seller_id === user?.uid || chatInfo.buyer_id === user?.uid
    : false;

  // Mark chat as read when opening chat room
  const markAsReadMutation = useMutation({
    mutationFn: async () => {
      if (!chatId || !user) return;
      const token = await user.getIdToken();
      const res = await fetch(`/api/chat/${chatId}/mark-read`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error("Failed to mark as read");
      return res.json();
    },
  });

  // Mark as read when chat room is opened and chat info is loaded
  useEffect(() => {
    if (chatInfo && user && chatId && isParticipant) {
      // Mark as read after a short delay to ensure messages are loaded
      const timer = setTimeout(() => {
        markAsReadMutation.mutate();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [chatInfo, user, chatId, isParticipant, markAsReadMutation]);

  // Mark as read when user scrolls to bottom (all messages visible)
  const handleMarkAsRead = useCallback(() => {
    if (chatId && user && !markAsReadMutation.isPending) {
      markAsReadMutation.mutate();
    }
  }, [chatId, user, markAsReadMutation]);

  if (!user) {
    return (
      <PageContainer size="default">
        <Card className="p-12 text-center">
          <p className="text-muted-foreground mb-4">
            채팅을 사용하려면 로그인이 필요합니다.
          </p>
          <Button onClick={() => setLocation("/")}>홈으로</Button>
        </Card>
      </PageContainer>
    );
  }

  if (!chatId) {
    return (
      <PageContainer size="default">
        <Card className="p-12 text-center">
          <p className="text-muted-foreground mb-4">유효하지 않은 채팅방 ID입니다.</p>
          <Button onClick={() => setLocation("/")}>홈으로</Button>
        </Card>
      </PageContainer>
    );
  }

  if (chatLoading) {
    return (
      <PageContainer size="default">
        <Card className="p-12 text-center">
          <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin text-muted-foreground" />
          <p className="text-muted-foreground">채팅방을 불러오는 중...</p>
        </Card>
      </PageContainer>
    );
  }

  if (!chatInfo) {
    return (
      <PageContainer size="default">
        <Card className="p-12 text-center">
          <p className="text-muted-foreground mb-4">채팅방을 찾을 수 없습니다.</p>
          <Button onClick={() => setLocation("/")}>홈으로</Button>
        </Card>
      </PageContainer>
    );
  }

  // isParticipant is already calculated above, check it here
  if (!isParticipant) {
    return (
      <PageContainer size="default">
        <Card className="p-12 text-center">
          <p className="text-muted-foreground mb-4">
            이 채팅방에 접근할 권한이 없습니다.
          </p>
          <Button onClick={() => setLocation("/")}>홈으로</Button>
        </Card>
      </PageContainer>
    );
  }

  const otherUser =
    chatInfo.seller_id === user.uid
      ? chatInfo.buyer
      : chatInfo.seller;

  return (
    <PageContainer size="default">
      <div className="flex flex-col h-[calc(100vh-200px)] max-h-[800px]">
        {/* Header */}
        <div className="flex items-center gap-4 p-4 border-b bg-background">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setLocation("/chat")}
            className="shrink-0"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-semibold text-lg truncate">
              {chatInfo.listing?.title || "채팅"}
            </h1>
            {otherUser && (
              <p className="text-sm text-muted-foreground truncate">
                {otherUser.name || "사용자"}
              </p>
            )}
          </div>
        </div>

        {/* AdSense 광고 - 상단 (채팅방 내) */}
        <div className="px-4 py-2 border-b">
          <GoogleAd slot="chat-room-top" format="horizontal" layout="inline" />
        </div>

        {/* Chat Room */}
        <div className="flex-1 min-h-0">
          {currentUserId ? (
            <ChatRoom
              chatId={chatId}
              currentUserId={currentUserId}
              onMarkAsRead={handleMarkAsRead}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  );
}

